import { useCallback } from 'react'

import { IActionRepeaterValue, IActionType } from '@makyoapp/makyo-common'

import { parseVariableTextSingleCell } from '@/core/app-builder/parser/variable-text'
import { isDescendant, parseStringToDescendant } from '@/lib/slate'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { isActionPlugin } from '@/context/ActionFormProviderV2'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { ActionDataType } from '@/shared/action.types'

import { getColumnInfoFromValue, getTextFromSlateValue } from '.'

export const useGenerateActionDescription = () => {
  const { dbClient, tables, language, userSpecific } = useDbClientContext()
  const { dialogs, overlays, pages, menus } = useAppBuilderContext()
  const { userEmail } = useAppAuthContext()
  const { activeLayerContainer, activeLayerTableId } = useActiveLayerContext()

  const generateActionDescription = useCallback(
    (value: IActionRepeaterValue, customPluginActions: any[]) => {
      const getTable = (id: string) => tables.find((tb) => tb.id === id)
      const getDialog = (id: string) => dialogs.find((d) => d.id === id)
      const getOverlay = (id: string) => overlays.find((o) => o.id === id)
      const getPage = (id: string) => pages.find((p) => p.id === id)
      const getMenu = (id: string) => menus.find((m) => m.id === id)

      // Handle plugin actions
      if (isActionPlugin(value)) {
        const pluginAction = customPluginActions.find((pa) => pa.type === value.type)
        return pluginAction?.description || value.type?.toString()
      }

      const isWait = value.type === IActionType.Wait
      const isCloseOverlay = value.type === IActionType.CloseOverlay
      const isDetailScreen = value.type === IActionType.ShowDetailsScreen
      const isLogout = value.type === IActionType.Logout

      const isTrigger = value.id === 'trigger-node'

      const settings = value.settings || {}
      const fallbackTableId = settings.tableId || activeLayerTableId

      if (isCloseOverlay || isDetailScreen || isLogout || isTrigger) return ''

      // Handle global-variable_set-global-variable action
      if (value.type === IActionType.SetGlobalVariable) {
        const variableName = settings.name
        if (variableName) {
          return variableName
        }
        return 'Set Global Variable'
      }

      // Handle form_update-item-screen action
      if (value.type === IActionType.FormUpdateItemScreen) {
        const screenType = settings.screenType
        if (screenType === 'page') {
          const pageTitle = activeLayerContainer?.title || 'Current Page'
          return getTextFromSlateValue(pageTitle as any)
        } else if (screenType === 'overlay') {
          const overlayTitle = activeLayerContainer?.title || 'Current Overlay'
          return getTextFromSlateValue(overlayTitle as any)
        }
        return 'Update Item Screen'
      }

      // Handle menu actions
      if (settings.menu) {
        const menu = getMenu(settings.menu)
        if (menu) {
          return `${menu.title || 'Unknown Menu'}`
        }
        return 'Unknown'
      }

      // Handle page/screen actions
      if (settings.screen) {
        const page = getPage(settings.screen)

        let pageTitle = page?.title || ''

        if (isDescendant(pageTitle)) {
          const node = parseStringToDescendant(pageTitle)
          pageTitle = parseVariableTextSingleCell(node, dbClient, language, userSpecific, userEmail)
        }

        return pageTitle
      }

      // Handle overlay actions
      if (settings.overlay) {
        const overlay = getOverlay(settings.overlay)
        if (overlay) {
          return `${overlay.title || 'Unknown Overlay'}`
        }
        return 'Unknown'
      }

      // Handle dialog actions
      if (settings.dialog) {
        const dialog = getDialog(settings.dialog)
        if (dialog) {
          const dialogName = getTextFromSlateValue(dialog.name)
          return `${dialogName || 'Unknown Dialog'}`
        }
        return 'Unknown'
      }

      // Handle generate-pdf action with filename
      if (value.type === IActionType.GeneratePDF) {
        if (settings.filename && Array.isArray(settings.filename)) {
          const text = getTextFromSlateValue(settings.filename)
          if (text && text.trim()) {
            return `PDF: ${text.trim()}`
          }
        }
        return 'Generate PDF'
      }

      if (value.type === IActionType.AddToCart) {
        if (settings.cartProductIdColumnId && settings.cartTableId) {
          const table = getTable(settings.cartTableId)
          const tableName = getTextFromSlateValue(table?.name || '') || 'Unknown Table'
          const column = table?.columns.find((col) => col.id === settings.cartProductIdColumnId)
          const columnName = getTextFromSlateValue(column?.name || '') || 'Unknown Column'
          return `${tableName} → ${columnName}`
        }
        return 'Add to Cart'
      }

      // Handle settings.value as object (like open-link with column reference)
      if ('value' in settings || (typeof settings.value === 'object' && 'value' in settings.value)) {
        const valueObj = typeof settings.value === 'object' ? settings.value : settings
        const tableId = valueObj.tableId
        const columnId = valueObj.value
        const dataType = valueObj.dataType

        if (tableId && columnId) {
          const table = getTable(tableId)
          const tableName = getTextFromSlateValue(table?.name || '') || 'Unknown Table'

          if (
            dataType === ActionDataType.SingleCell ||
            dataType === ActionDataType.ColumnData ||
            dataType === ActionDataType.CurrentRow
          ) {
            const column = table?.columns.find((col) => col.id === columnId)
            const columnName = getTextFromSlateValue(column?.name || '') || 'Unknown Column'
            return `${tableName} → ${columnName}`
          }
        }
      }

      // Handle direct value (like URL, file path, etc.)
      if (settings.value && typeof settings.value === 'string') {
        // Check if it's a URL
        if (settings.value.startsWith('http://') || settings.value.startsWith('https://')) {
          // Extract filename from URL if possible
          try {
            const url = new URL(settings.value)
            const pathname = url.pathname
            const filename = pathname.split('/').pop()
            if (filename) {
              return `File: ${decodeURIComponent(filename)}`
            }
          } catch (e) {
            // If URL parsing fails, just return the URL
          }
          return `URL: ${settings.value}`
        }

        // Check if it's a file path
        if (settings.value.includes('/') || settings.value.includes('\\')) {
          const filename = settings.value.split(/[/\\]/).pop()
          return `File: ${filename || settings.value}`
        }

        // Handle misc_clipboard and similar actions with dataType + tableId + value structure
        if (settings.dataType && settings.tableId && settings.value && typeof settings.value === 'string') {
          const table = getTable(settings.tableId)
          const tableName = getTextFromSlateValue(table?.name || '') || 'Unknown Table'

          // Check if it's a column reference based on dataType
          if (settings.dataType === ActionDataType.SingleCell || settings.dataType === ActionDataType.ColumnData) {
            const column = table?.columns.find((col) => col.id === settings.value)
            const columnName = getTextFromSlateValue(column?.name || '') || 'Unknown Column'
            return `${tableName} → ${columnName}`
          }
          // For other dataTypes, return the value as is
          return settings.value
        }
        // For other string values, return as is
        return settings.value
      }

      if (settings.value && typeof settings.value === 'number') {
        return `${settings.value?.toString()}${isWait ? ' seconds' : ''}`
      }

      // Handle Slate text values (like snackbar messages)
      if (settings.value && Array.isArray(settings.value)) {
        const text = getTextFromSlateValue(settings.value)
        if (text) return text
      }

      // Handle action input fields (email, phone, cc, bcc, subject, message, etc.)
      const inputFields = ['email', 'phone', 'cc', 'bcc', 'subject', 'message', 'url', 'filePath']
      for (const field of inputFields) {
        if (settings[field] && settings[field].value !== undefined && settings[field].value !== null) {
          const fieldValue = settings[field].value as any
          const dataType = settings[field].dataType as any

          // If value is Slate content (array), render as text
          if (Array.isArray(fieldValue)) {
            const text = getTextFromSlateValue(fieldValue)
            if (text) return text
          }

          // Try to resolve to Table → Column using best-known tableId
          const resolved = getColumnInfoFromValue(String(fieldValue), dataType, tables, fallbackTableId)
          if (resolved.isColumn) {
            return `${resolved.tableName} → ${resolved.columnName}`
          }

          // If fieldValue is an object with { tableId, value, dataType }
          if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
            const valueObj = fieldValue
            const tableId = valueObj.tableId || fallbackTableId
            const columnId = valueObj.value
            const valueDataType = valueObj.dataType ?? dataType

            if (tableId && columnId) {
              const table = getTable(tableId)
              const tableName = getTextFromSlateValue(table?.name || '') || 'Unknown Table'
              if (valueDataType === ActionDataType.SingleCell || valueDataType === ActionDataType.ColumnData) {
                const column = table?.columns.find((col) => col.id === columnId)
                const columnName = getTextFromSlateValue(column?.name || '') || 'Unknown Column'
                return `${tableName} → ${columnName}`
              }
            }
            return `${valueObj.value}`
          }

          // As a last resort, try global lookup by column ID across all tables
          if (typeof fieldValue === 'string' && fieldValue.trim()) {
            const containingTable = tables.find((tb) => tb.columns.some((col) => col.id === fieldValue))
            if (containingTable) {
              const column = containingTable.columns.find((col) => col.id === fieldValue)
              const tableName = getTextFromSlateValue(containingTable.name || '') || 'Unknown Table'
              const columnName = getTextFromSlateValue(column?.name || '') || 'Unknown Column'
              return `${tableName} → ${columnName}`
            }
          }

          // Fallback: stringify primitive value (avoids showing raw IDs as non-strings)
          return `${fieldValue}`
        }
      }

      // Handle actions with table data
      if (settings.tableId) {
        const table = getTable(settings.tableId)
        const tableName = table?.name || 'Unknown Table'

        // Handle actions with column data
        if (settings.col) {
          const column = table?.columns.find((col) => col.id === settings.col)
          const columnName = column?.name || 'Unknown Column'

          // Handle actions with specific data/value
          if (settings.data?.value !== undefined) {
            return `${columnName}: ${settings.data.value}`
          }

          return `${columnName}`
        }

        return tableName
      }

      // Handle actions with specific data but no table
      if (settings.data?.value !== undefined) {
        return `Value: ${settings.data.value}`
      }

      // Handle actions with URL/file paths
      if (settings.url) {
        return `URL: ${settings.url}`
      }

      if (settings.filePath) {
        return `File: ${settings.filePath}`
      }

      // Handle actions with screen/page names
      if (settings.screenName) {
        return `Screen: ${settings.screenName}`
      }

      if (settings.pageTitle) {
        return `Page: ${settings.pageTitle}`
      }

      // Fallback to action type name
      return value.type?.toString()
    },
    [
      activeLayerContainer?.title,
      activeLayerTableId,
      dbClient,
      dialogs,
      menus,
      overlays,
      pages,
      tables,
      userEmail,
      userSpecific,
      language,
    ]
  )

  return { generateActionDescription }
}
